import React from 'react';
import { Link } from 'react-router-dom';

export interface ClassificationTagCloudProps {
  classifications: string[];
  frequency?: Record<string, number>;
  maxTags?: number;
  className?: string;
}

const ClassificationTagCloud: React.FC<ClassificationTagCloudProps> = ({
  classifications,
  frequency = {},
  maxTags = 100,
  className = '',
}) => {
  // バッジの数値に基づいてタグのサイズを計算（絶対値ベース）
  const getTagSize = (classification: string): string => {
    const freq = frequency[classification] || 1;

    // バッジの数値に基づく絶対的なサイズ決定（フォントサイズとパディングを段階的に調整）
    if (freq >= 200) return 'text-2xl px-6 py-3'; // 200以上：最大
    if (freq >= 100) return 'text-xl px-5 py-2.5'; // 100-199：大
    if (freq >= 50) return 'text-lg px-4 py-2'; // 50-99：中大
    if (freq >= 30) return 'text-base px-3.5 py-1.5'; // 30-49：中
    if (freq >= 20) return 'text-sm px-3 py-1.5'; // 20-29：中小
    if (freq >= 15) return 'text-xs px-2.5 py-1'; // 15-19：小
    if (freq >= 10) return 'text-xs px-2 py-0.75'; // 10-14：より小
    if (freq >= 7) return 'text-xs px-1.5 py-0.5'; // 7-9：最小
    if (freq >= 5) return 'text-xs px-1 py-0.5'; // 5-6：最小
    return 'text-xs px-1 py-0.5'; // 4以下：最小
  };

  // バッジの数値に基づいてタグの色を計算（絶対値ベース）
  const getTagStyle = (classification: string): { backgroundColor: string; color: string } => {
    const freq = frequency[classification] || 1;

    // 指定された10色のカラーパレット（高頻度→低頻度の順）
    const colors = [
      '#A132DB', // 紫（最高頻度）
      '#4C8668', // 緑
      '#32DB85', // 明るい緑
      '#51864C', // ダークグリーン
      '#DB329D', // ピンク
      '#42DB32', // 明るい黄緑
      '#9C4B7E', // 紫ピンク
      '#312E33', // ダークグレー
      '#5C4353', // ブラウン
      '#724C86', // ダークパープル（最低頻度）
    ];

    // バッジの数値に基づいて色インデックスを決定（絶対値ベース）
    let colorIndex = 0;
    if (freq >= 200)
      colorIndex = 0; // 紫
    else if (freq >= 100)
      colorIndex = 1; // 緑
    else if (freq >= 50)
      colorIndex = 2; // 明るい緑
    else if (freq >= 30)
      colorIndex = 3; // ダークグリーン
    else if (freq >= 20)
      colorIndex = 4; // ピンク
    else if (freq >= 15)
      colorIndex = 5; // 明るい黄緑
    else if (freq >= 10)
      colorIndex = 6; // 紫ピンク
    else if (freq >= 7)
      colorIndex = 7; // ダークグレー
    else if (freq >= 5)
      colorIndex = 8; // ブラウン
    else colorIndex = 9; // ダークパープル

    const backgroundColor = colors[colorIndex];

    // テキストの色を決定（背景色の明度に基づいて白または黒を選択）
    // 明るい色（明るい緑、明るい黄緑）には黒テキスト、暗い色には白テキスト
    const lightColors = [2, 5]; // 明るい緑、明るい黄緑のインデックス
    const color = lightColors.includes(colorIndex) ? '#000000' : '#FFFFFF';

    return { backgroundColor, color };
  };

  // アルファベット順の降順（z→a）でソート
  const sortedClassifications = [...classifications].sort((a, b) => b.localeCompare(a));

  // 頻度が3より大きい分類のみをフィルタリング
  const filteredClassifications = sortedClassifications.filter(classification => {
    const freq = frequency[classification] || 0;
    return freq > 3;
  });

  const displayClassifications = filteredClassifications.slice(0, maxTags);

  return (
    <div className={`flex flex-wrap gap-4 ${className}`}>
      {displayClassifications.map((classification, index) => {
        const tagStyle = getTagStyle(classification);
        return (
          <Link
            key={`${classification}-${index}`}
            to={`/creatures/classification/${encodeURIComponent(classification)}`}
            className={`
              relative inline-flex items-center rounded-full font-medium transition-all duration-200
              ${getTagSize(classification)}
              hover:scale-105 hover:shadow-md px-6
            `}
            style={{
              backgroundColor: tagStyle.backgroundColor,
              color: tagStyle.color,
            }}
          >
            {classification}
            {frequency[classification] && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-white bg-opacity-90 text-gray-800 border border-gray-300 px-1">
                {frequency[classification]}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
};

export default ClassificationTagCloud;
